<template>
  <div class="article-form">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">Create Article</h4>
      <router-link :to="{ name: 'articles-list' }" class="btn btn-default btn-sm">Back to list</router-link>
    </div>

    <div class="article-form__layout">
      <!-- Main content -->
      <div class="article-form__main">
        <card title="Content">
          <div class="row mb-3">
            <div class="col-md-2">
              <label>Language *</label>
              <el-select v-model="form.lang" placeholder="Lang" style="width: 100%">
                <el-option v-for="l in langs" :key="l" :label="l.toUpperCase()" :value="l" />
              </el-select>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-md-6">
              <label>Title *</label>
              <el-input v-model="form.name" placeholder="Article title" />
            </div>
            <div class="col-md-6">
              <label>Alias *</label>
              <el-input v-model="form.alias" placeholder="article-url-alias" />
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-12">
              <label>H1 (page heading)</label>
              <el-input v-model="form.h1" placeholder="Leave empty to reuse the title as H1" />
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-12">
              <label>Short description (lead under the H1)</label>
              <el-input v-model="form.short_description" type="textarea" :rows="3" maxlength="320" show-word-limit placeholder="One or two plain-text sentences" />
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-12">
              <label>Meta description</label>
              <el-input v-model="form.meta_description" type="textarea" :rows="3" maxlength="320" show-word-limit placeholder="SEO meta description for search and social previews" />
            </div>
          </div>

          <div class="article-form__editor-wrap mb-3">
            <label>Text *</label>
            <ckeditor
              :key="`text-${editorRenderKey}`"
              v-model="form.text"
              :config="ckeditorConfig"
            ></ckeditor>
          </div>

          <div class="mb-3">
            <label>Key takeaways</label>
            <div v-for="(item, index) in form.key_takeaways" :key="index" class="d-flex align-items-start mb-2">
              <el-input :value="item" type="textarea" :autosize="{ minRows: 1 }" placeholder="One takeaway, plain text" class="mr-2" @input="v => $set(form.key_takeaways, index, v)" />
              <el-button size="mini" type="danger" icon="el-icon-delete" @click="form.key_takeaways.splice(index, 1)" />
            </div>
            <el-button size="mini" @click="form.key_takeaways.push('')">Add takeaway</el-button>
            <small class="text-muted d-block mt-1">Rendered as the "Key takeaways" bullet list; an empty list hides the block.</small>
          </div>

          <div class="mb-3">
            <label>Table of contents</label>
            <div v-for="(item, index) in form.table_of_contents" :key="index" class="d-flex align-items-start mb-2">
              <div class="flex-grow-1 mr-2">
                <el-select v-model="item.anchor" filterable placeholder="Target H2 anchor" style="width: 100%" class="mb-1">
                  <el-option v-for="heading in tocHeadings" :key="heading.anchor" :label="`${heading.title}  (#${heading.anchor})`" :value="heading.anchor" />
                </el-select>
                <el-input v-model="item.title" type="textarea" :autosize="{ minRows: 1 }" placeholder="Link label (defaults to the H2 title)" />
              </div>
              <el-button size="mini" type="danger" icon="el-icon-delete" @click="form.table_of_contents.splice(index, 1)" />
            </div>
            <el-button size="mini" @click="form.table_of_contents.push({ title: '', anchor: '' })">Add section</el-button>
            <small class="text-muted d-block mt-1">Each entry links to an existing <code>&lt;h2 id="…"&gt;</code> in the article text. An empty list hides the block.</small>
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <label>Draft Title</label>
              <el-input v-model="form.draft.title" placeholder="Draft title" />
            </div>
          </div>

          <div class="article-form__editor-wrap mb-3">
            <label>Draft Text</label>
            <ckeditor
              :key="`draft-text-${editorRenderKey}`"
              v-model="form.draft.text"
              :config="ckeditorConfigDraftText"
            ></ckeditor>
          </div>

          <div class="article-form__editor-wrap mb-3">
            <label>References</label>
            <ckeditor
              :key="`references-${editorRenderKey}`"
              v-model="form.references"
              :config="ckeditorConfigReferences"
            ></ckeditor>
          </div>
        </card>
      </div>

      <!-- Sidebar -->
      <div class="article-form__sidebar">
        <card title="Relations">
          <label>Author</label>
          <el-select v-model="form.author" value-key="id" filterable clearable placeholder="Select author" style="width: 100%" class="mb-2">
            <el-option v-for="u in users" :key="u.id" :label="`${u.first_name || u.firstName || ''} ${u.last_name || u.lastName || ''}`" :value="u" />
          </el-select>

          <label>Editor</label>
          <el-select v-model="form.editor" value-key="id" filterable clearable placeholder="Select editor" style="width: 100%" class="mb-2">
            <el-option v-for="u in users" :key="u.id" :label="`${u.first_name || u.firstName || ''} ${u.last_name || u.lastName || ''}`" :value="u" />
          </el-select>

          <label>Illness Kind</label>
          <el-select v-model="form.illness_kind" value-key="id" filterable clearable placeholder="Select" style="width: 100%" class="mb-2">
            <el-option v-for="item in illnessKinds" :key="item.id" :label="item.name && item.name.en || item.name && item.name.ru || item.id" :value="item" />
          </el-select>

          <label>Illness</label>
          <el-select v-model="form.illness" value-key="id" filterable clearable placeholder="Select" style="width: 100%" class="mb-2">
            <el-option v-for="item in illnesses" :key="item.id" :label="item.name && item.name.en || item.name && item.name.ru || item.id" :value="item" />
          </el-select>

          <label>Operation</label>
          <el-select v-model="form.operation" value-key="id" filterable clearable placeholder="Select" style="width: 100%" class="mb-2">
            <el-option v-for="item in operations" :key="item.id" :label="item.name && item.name.en || item.name && item.name.ru || item.id" :value="item" />
          </el-select>

          <label>Diagnostic</label>
          <el-select v-model="form.diagnostic" value-key="id" filterable clearable placeholder="Select" style="width: 100%" class="mb-2">
            <el-option v-for="item in diagnostics" :key="item.id" :label="item.name && item.name.en || item.name && item.name.ru || item.id" :value="item" />
          </el-select>

          <label>Country</label>
          <el-select v-model="form.country" value-key="id" filterable clearable placeholder="Select" style="width: 100%" class="mb-2">
            <el-option v-for="item in countries" :key="item.id" :label="item.name && item.name.en || item.name && item.name.ru || item.id" :value="item" />
          </el-select>

          <label>Tags</label>
          <el-select v-model="form.tags" value-key="id" multiple filterable placeholder="Select tags" style="width: 100%" class="mb-2">
            <el-option v-for="tag in tags" :key="tag.id" :label="tag.name && tag.name.en || tag.id" :value="tag" />
          </el-select>
        </card>

        <card title="Images">
          <p class="small text-muted">Listing images — shown in the blog list and as the article preview.</p>
          <div class="row">
            <div class="col-md-6 text-center mb-3">
              <p class="small text-muted mb-1">Main image (full width)</p>
              <small class="text-muted d-block mb-2">JPG/PNG, fitted into 1200×450</small>
              <img v-if="fileMainDataUrl" :src="fileMainDataUrl" class="article-form__img mb-2" />
              <div>
                <button type="button" class="article-form__upload-btn" @click.prevent="$refs.fileMain.click()">Upload</button>
              </div>
              <input type="file" ref="fileMain" accept="image/jpeg,image/png" @change="handleFileUpload($event, 'main')" style="display:none" />
              <small v-if="fileMain" class="text-success">{{ fileMain.name }}</small>
            </div>
            <div class="col-md-6 text-center mb-3">
              <p class="small text-muted mb-1">Preview image (thumbnail)</p>
              <small class="text-muted d-block mb-2">JPG/PNG, fitted into 290×210</small>
              <img v-if="filePreviewDataUrl" :src="filePreviewDataUrl" class="article-form__img mb-2" />
              <div>
                <button type="button" class="article-form__upload-btn" @click.prevent="$refs.filePreview.click()">Upload</button>
              </div>
              <input type="file" ref="filePreview" accept="image/jpeg,image/png" @change="handleFileUpload($event, 'preview')" style="display:none" />
              <small v-if="filePreview" class="text-success">{{ filePreview.name }}</small>
            </div>
          </div>
          <hr />
          <p class="small text-muted">Article header images (new /article/ design). Optional — the header renders without an image if empty.</p>
          <div class="row">
            <div class="col-md-6 text-center mb-3">
              <p class="small text-muted mb-1">Header — vertical (desktop)</p>
              <small class="text-muted d-block mb-2">Portrait, fitted into 900×1200; sits right of the title on desktop</small>
              <img v-if="fileHeaderVerticalDataUrl" :src="fileHeaderVerticalDataUrl" class="article-form__img mb-2" />
              <div>
                <button type="button" class="article-form__upload-btn" @click.prevent="$refs.fileHeaderVertical.click()">Upload</button>
              </div>
              <input type="file" ref="fileHeaderVertical" accept="image/jpeg,image/png" @change="handleFileUpload($event, 'header_vertical')" style="display:none" />
              <small v-if="fileHeaderVertical" class="text-success">{{ fileHeaderVertical.name }}</small>
            </div>
            <div class="col-md-6 text-center mb-3">
              <p class="small text-muted mb-1">Header — horizontal (mobile)</p>
              <small class="text-muted d-block mb-2">Landscape, fitted into 1200×675; sits under the author block on mobile</small>
              <img v-if="fileHeaderHorizontalDataUrl" :src="fileHeaderHorizontalDataUrl" class="article-form__img mb-2" />
              <div>
                <button type="button" class="article-form__upload-btn" @click.prevent="$refs.fileHeaderHorizontal.click()">Upload</button>
              </div>
              <input type="file" ref="fileHeaderHorizontal" accept="image/jpeg,image/png" @change="handleFileUpload($event, 'header_horizontal')" style="display:none" />
              <small v-if="fileHeaderHorizontal" class="text-success">{{ fileHeaderHorizontal.name }}</small>
            </div>
          </div>
        </card>

        <card title="Settings">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <span>Active</span>
            <el-switch v-model="form.active" active-color="#a3cc0e" inactive-color="#f96332" />
          </div>
        </card>

        <div class="mt-3">
          <el-button type="primary" :loading="saving" @click="create" style="width: 100%">Create Article</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import APIArticles from "@/api/modules/articles";

export default {
  data() {
    return {
      ckeditorConfig: { allowedContent: true },
      ckeditorConfigDraftText: { allowedContent: true },
      ckeditorConfigReferences: { allowedContent: true },
      editorRenderKey: 0,
      saving: false,
      fileMain: null,
      filePreview: null,
      fileHeaderVertical: null,
      fileHeaderHorizontal: null,
      fileMainDataUrl: null,
      filePreviewDataUrl: null,
      fileHeaderVerticalDataUrl: null,
      fileHeaderHorizontalDataUrl: null,
      form: {
        lang: "en",
        name: "",
        h1: "",
        short_description: "",
        meta_description: "",
        key_takeaways: [],
        table_of_contents: [],
        alias: "",
        text: "",
        references: "",
        draft: { title: "", text: "" },
        active: true,
        author: null,
        editor: null,
        illness_kind: null,
        illness: null,
        operation: null,
        diagnostic: null,
        country: null,
        tags: [],
      },
    };
  },
  computed: {
    ...mapState("articles", ["tags", "users"]),
    ...mapState("cmsApiCatalogs", {
      illnessKinds: "illness-kinds",
      illnesses: "illnesses",
      operations: "operations",
      diagnostics: "diagnostics",
      countries: "countries",
      domains: "domains",
    }),
    langs() {
      return (this.domains || []).map(({ language }) => language).filter(Boolean);
    },
    tocHeadings() {
      const headings = [];
      const seen = new Set();
      const regex = /<h2\b[^>]*\bid\s*=\s*(["'])([^"']+)\1[^>]*>([\s\S]*?)<\/h2>/gi;
      let match;
      while ((match = regex.exec(this.form.text || "")) !== null) {
        const anchor = match[2].trim();
        if (!anchor || seen.has(anchor)) continue;
        const title = match[3].replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
        headings.push({ anchor, title });
        seen.add(anchor);
      }
      return headings;
    },
  },
  methods: {
    ...mapActions("articles", ["upsertArticle", "getTags", "getUsers"]),
    ...mapActions("cmsApiCatalogs", ["getIllnessKinds", "getIllnesses", "getOperations", "getDiagnostics", "getCountries", "getDomains"]),
    /** Upload slot per blog-api image type: [File data key, preview data-URL key]. */
    fileSlot(type) {
      return {
        main: ["fileMain", "fileMainDataUrl"],
        preview: ["filePreview", "filePreviewDataUrl"],
        header_vertical: ["fileHeaderVertical", "fileHeaderVerticalDataUrl"],
        header_horizontal: ["fileHeaderHorizontal", "fileHeaderHorizontalDataUrl"],
      }[type];
    },
    selectedImageFiles() {
      const files = {};
      ["main", "preview", "header_vertical", "header_horizontal"].forEach(type => {
        const [fileKey] = this.fileSlot(type);
        if (this[fileKey] instanceof File) files[type] = this[fileKey];
      });
      return files;
    },
    handleFileUpload(event, type) {
      const file = event.target.files[0];
      if (!file) return;
      if (!["image/jpeg", "image/png"].includes(file.type)) {
        return alert("Only .jpg, .jpeg, .png files are allowed");
      }
      const [fileKey, dataUrlKey] = this.fileSlot(type);
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        this[dataUrlKey] = reader.result;
        this[fileKey] = file;
      });
      reader.readAsDataURL(file);
    },
    formatAuthor(author) {
      if (!author || !author.id) return null;
      return {
        id: author.id,
        email: author.email || "",
        first_name: author.firstName || author.first_name || "",
        last_name: author.lastName || author.last_name || "",
        metadata: author.metadata || {},
        phone: author.phone || "",
        avatar: author.avatar || "",
      };
    },
    formatCountry(country) {
      if (!country) return null;
      return { ...country, iso_code: country.code || country.iso_code || "" };
    },
    formatIllnessKind(ik) {
      if (!ik) return null;
      return { alias: ik.alias, id: parseInt(ik.id), name: ik.name };
    },
    normalizedTableOfContents() {
      return (this.form.table_of_contents || []).map(item => ({ title: ((item && item.title) || "").trim(), anchor: ((item && item.anchor) || "").trim() })).filter(item => item.title || item.anchor);
    },
    validateTableOfContents(items) {
      if (items.some(item => !item.title || !item.anchor)) {
        this.$notify({ type: "warning", title: "Each table-of-contents entry needs a label and a target H2 anchor" });
        return false;
      }
      const anchors = new Set(this.tocHeadings.map(item => item.anchor));
      if (items.some(item => !anchors.has(item.anchor))) {
        this.$notify({ type: "warning", title: "Each table-of-contents anchor must target an H2 id in the article text" });
        return false;
      }
      if (new Set(items.map(item => item.anchor)).size !== items.length) {
        this.$notify({ type: "warning", title: "A table-of-contents target can only be used once" });
        return false;
      }
      return true;
    },
    async create() {
      if (!this.form.lang || !this.form.name || !this.form.alias) {
        return this.$notify({ type: "warning", title: "Language, Title and Alias are required" });
      }
      if (!this.form.author || !this.form.author.id) {
        return this.$notify({ type: "warning", title: "Author is required" });
      }
      const tableOfContents = this.normalizedTableOfContents();
      if (!this.validateTableOfContents(tableOfContents)) return;
      this.saving = true;
      try {
        const id = `${this.form.lang}:${this.form.alias}`;
        const payload = {
          id,
          name: this.form.name,
          h1: (this.form.h1 || "").trim(),
          short_description: (this.form.short_description || "").trim(),
          meta_description: (this.form.meta_description || "").trim(),
          key_takeaways: (this.form.key_takeaways || []).map(t => (t || "").trim()).filter(Boolean),
          table_of_contents: tableOfContents,
          text: this.form.text,
          hide: !this.form.active,
          references: this.form.references,
          draft: { title: this.form.draft.title, text: this.form.draft.text },
          author: this.formatAuthor(this.form.author),
          editor: this.formatAuthor(this.form.editor),
          illness_kind: this.formatIllnessKind(this.form.illness_kind),
          illness: this.form.illness || null,
          operation: this.form.operation || null,
          diagnostic: this.form.diagnostic || null,
          country: this.formatCountry(this.form.country),
          tags: this.form.tags,
          image: { main: "", preview: "", header_vertical: "", header_horizontal: "" },
        };

        await this.upsertArticle(payload);

        // Upload whichever images were selected (blog-api accepts any subset of types)
        const files = this.selectedImageFiles();
        if (Object.keys(files).length) {
          await APIArticles.addImage({ lang: this.form.lang, articleAlias: this.form.alias, files });
        }

        this.$notify({ type: "success", title: "Article created" });
        this.$router.push({ name: "articles-edit", params: { id } });
      } catch (e) {
        this.$notify({ type: "warning", title: "Error creating article" });
      } finally {
        this.saving = false;
      }
    },
  },
  async created() {
    await Promise.all([
      this.getDomains(),
      this.getTags(),
      this.getUsers(),
      this.getIllnessKinds({ perPage: 1500 }),
      this.getIllnesses({ perPage: 1500 }),
      this.getOperations({ perPage: 1500 }),
      this.getDiagnostics({ perPage: 1500 }),
      this.getCountries({ perPage: 1500 }),
    ]);
    if (this.langs.length && !this.langs.includes(this.form.lang)) {
      this.form.lang = this.langs[0];
    }
  },
};
</script>

<style scoped>
.article-form__layout {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}
.article-form__main {
  flex: 1;
  min-width: 0;
}
.article-form__sidebar {
  width: 320px;
  flex-shrink: 0;
}
.article-form__img {
  max-width: 100%;
  max-height: 120px;
  border-radius: 8px;
  object-fit: contain;
}
.article-form__upload-btn {
  display: inline-block;
  padding: 8px 20px;
  background: #f4f8fb;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
}
.article-form__upload-btn:hover {
  background: #e9ecef;
}
</style>
